package dev.wyns.ventrova.ui.android

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.app.AppEnvironment
import dev.wyns.ventrova.app.AppMessage
import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.data.model.MenuItem
import dev.wyns.ventrova.platform.displayUrl
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.action_route
import dev.wyns.ventrova.resources.action_route_walk
import dev.wyns.ventrova.resources.ic_arrow_back
import dev.wyns.ventrova.resources.ic_call
import dev.wyns.ventrova.resources.ic_directions_filled
import dev.wyns.ventrova.resources.ic_expand_less
import dev.wyns.ventrova.resources.ic_expand_more
import dev.wyns.ventrova.resources.ic_favorite
import dev.wyns.ventrova.resources.ic_favorite_filled
import dev.wyns.ventrova.resources.ic_language
import dev.wyns.ventrova.resources.ic_location_on
import dev.wyns.ventrova.resources.ic_schedule
import dev.wyns.ventrova.resources.ic_share
import dev.wyns.ventrova.resources.opening_hours
import dev.wyns.ventrova.resources.opening_hours_today
import dev.wyns.ventrova.resources.report_action
import dev.wyns.ventrova.resources.report_question
import dev.wyns.ventrova.ui.components.BannerStyle
import dev.wyns.ventrova.ui.components.FryOilBanner
import dev.wyns.ventrova.ui.components.LevelBadge
import dev.wyns.ventrova.ui.components.MenuDishes
import dev.wyns.ventrova.ui.components.MenuStyle
import dev.wyns.ventrova.ui.components.PlaceThumbnail
import dev.wyns.ventrova.ui.components.VIcon
import dev.wyns.ventrova.ui.components.WeekHours
import dev.wyns.ventrova.ui.components.bodyStyle
import dev.wyns.ventrova.ui.components.cuisineLabel
import dev.wyns.ventrova.ui.components.menuTitle
import dev.wyns.ventrova.ui.theme.VentrovaColors
import dev.wyns.ventrova.ui.theme.currentDayOfWeek
import dev.wyns.ventrova.ui.theme.displayStyle
import dev.wyns.ventrova.ui.theme.hoursOnDay
import dev.wyns.ventrova.ui.theme.label
import dev.wyns.ventrova.ui.theme.openStatusText
import dev.wyns.ventrova.ui.theme.walkLabel
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.stringResource

/** Full-screen place detail, as in the Android design. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun AndroidDetail(item: PlaceItem, menu: List<MenuItem>?, env: AppEnvironment) {
    val place = item.place
    val actions = env.dependencies.externalActions
    val viewModel = env.viewModel
    var hoursOpen by rememberSaveable(item.id) { mutableStateOf(false) }

    Box(Modifier.fillMaxSize().background(VentrovaColors.Canvas)) {
        Column(Modifier.fillMaxSize().verticalScroll(rememberScrollState()).navigationBarsPadding().padding(bottom = 40.dp)) {
            PlaceThumbnail(
                place,
                Modifier.fillMaxWidth().height(280.dp),
                RoundedCornerShape(bottomStart = 28.dp, bottomEnd = 28.dp),
                iconSize = 56.dp,
            )
            // Lets the name, address, hours and dishes be selected and copied.
            SelectionContainer {
                Column(Modifier.padding(start = 20.dp, end = 20.dp, top = 20.dp)) {
                    Text(place.name, style = displayStyle(32, lineHeight = 1.05f))
                    Text(
                        listOfNotNull(stringResource(place.category.label), place.cuisineLabel()).joinToString(" · "),
                        style = bodyStyle(15.sp, color = VentrovaColors.Secondary),
                        modifier = Modifier.padding(top = 6.dp),
                    )
                    FlowRow(Modifier.padding(top = 14.dp), horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        LevelBadge(
                            place.veganLevel,
                            Modifier.height(32.dp),
                            shape = RoundedCornerShape(8.dp),
                            fontSize = 14.sp,
                            glyphSize = 12.dp,
                            contentPadding = PaddingValues(start = 9.dp, end = 12.dp),
                        )
                        openStatusText(item.openStatus)?.let { status ->
                            Row(
                                Modifier.height(32.dp).border(1.dp, VentrovaColors.Outline, RoundedCornerShape(8.dp)).padding(horizontal = 12.dp),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Text(
                                    buildAnnotatedString {
                                        withStyle(SpanStyle(color = status.color, fontWeight = FontWeight.SemiBold)) { append(status.status) }
                                        status.change?.let { append(" · $it") }
                                    },
                                    style = bodyStyle(14.sp),
                                )
                            }
                        }
                    }
                    FryOilBanner(
                        place,
                        onReport = { viewModel.requestFryOilCheck(place.id) },
                        modifier = Modifier.padding(top = 12.dp).fillMaxWidth(),
                        style = BannerStyle.Material,
                    )

                    DisableSelection {
                        Row(Modifier.padding(top = 18.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Surface(
                                onClick = { actions.openDirections(place) },
                                shape = RoundedCornerShape(26.dp),
                                color = VentrovaColors.Accent,
                                contentColor = Color.White,
                                modifier = Modifier.weight(1.4f).height(52.dp),
                            ) {
                                Row(horizontalArrangement = Arrangement.Center, verticalAlignment = Alignment.CenterVertically) {
                                    VIcon(Res.drawable.ic_directions_filled, size = 22.dp)
                                    Spacer(Modifier.size(8.dp))
                                    val route = item.distanceMeters?.let { stringResource(Res.string.action_route_walk, walkLabel(it)) }
                                        ?: stringResource(Res.string.action_route)
                                    Text(route, style = bodyStyle(15.sp, FontWeight.SemiBold, Color.White))
                                }
                            }
                            place.phone?.let { phone -> TonalIconButton(Res.drawable.ic_call) { actions.call(phone) } }
                            place.website?.let { url -> TonalIconButton(Res.drawable.ic_language) { actions.openWebsite(url) } }
                            val favorite = item.isFavorite
                            val radius by animateDpAsState(if (favorite) 26.dp else 16.dp)
                            val background by animateColorAsState(if (favorite) VentrovaColors.Favorite else VentrovaColors.AccentContainerSoft)
                            Surface(
                                onClick = { viewModel.toggleFavorite(item.id) },
                                shape = RoundedCornerShape(radius),
                                color = background,
                                contentColor = if (favorite) Color.White else VentrovaColors.OnAccentContainerAlt,
                                modifier = Modifier.size(52.dp),
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    VIcon(if (favorite) Res.drawable.ic_favorite_filled else Res.drawable.ic_favorite, size = 22.dp)
                                }
                            }
                        }
                    }

                    if (place.menu != null) {
                        Column(Modifier.padding(top = 16.dp).fillMaxWidth().clip(RoundedCornerShape(24.dp)).background(VentrovaColors.Sand).padding(start = 18.dp, end = 18.dp, top = 18.dp, bottom = 8.dp)) {
                            Text(menuTitle(place), style = bodyStyle(16.sp, FontWeight.SemiBold), modifier = Modifier.padding(bottom = 6.dp))
                            MenuDishes(place, menu, MenuStyle.Material)
                        }
                    }

                    place.openingHours?.let { hours ->
                        val today = currentDayOfWeek()
                        Column(
                            Modifier
                                .padding(top = 16.dp)
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(24.dp))
                                .background(VentrovaColors.Sand)
                                .clickable { hoursOpen = !hoursOpen }
                                .padding(horizontal = 18.dp, vertical = 16.dp),
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                                VIcon(Res.drawable.ic_schedule, tint = openStatusText(item.openStatus)?.color ?: VentrovaColors.Secondary)
                                Column(Modifier.weight(1f)) {
                                    Text(stringResource(Res.string.opening_hours), style = bodyStyle(16.sp, FontWeight.SemiBold))
                                    Text(stringResource(Res.string.opening_hours_today, hoursOnDay(hours, today)), style = bodyStyle(14.sp, color = VentrovaColors.Secondary))
                                }
                                VIcon(if (hoursOpen) Res.drawable.ic_expand_less else Res.drawable.ic_expand_more, tint = VentrovaColors.Secondary)
                            }
                            if (hoursOpen) WeekHours(item, today, Modifier.padding(start = 38.dp, top = 10.dp), rowPadding = 5.dp)
                        }
                    }

                    val contacts = listOfNotNull(
                        place.address.formatted()?.let { Triple(Res.drawable.ic_location_on, it, null as (() -> Unit)?) },
                        place.phone?.let { Triple(Res.drawable.ic_call, it, { actions.call(it) }) },
                        place.website?.let { Triple(Res.drawable.ic_language, displayUrl(it), { actions.openWebsite(it) }) },
                    )
                    if (contacts.isNotEmpty()) {
                        Column(Modifier.padding(top = 12.dp).fillMaxWidth().clip(RoundedCornerShape(24.dp)).background(VentrovaColors.Sand)) {
                            contacts.forEach { (icon, text, onClick) ->
                                Row(
                                    Modifier.fillMaxWidth().let { if (onClick != null) it.clickable(onClick = onClick) else it }.padding(horizontal = 18.dp, vertical = 14.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(14.dp),
                                ) {
                                    VIcon(icon, tint = VentrovaColors.Secondary)
                                    Text(text, style = bodyStyle(15.sp, color = if (icon == Res.drawable.ic_language) VentrovaColors.AccentDark else VentrovaColors.Ink))
                                }
                            }
                        }
                    }

                    Text(
                        buildAnnotatedString {
                            append(stringResource(Res.string.report_question) + " ")
                            withStyle(SpanStyle(color = VentrovaColors.AccentDark, fontWeight = FontWeight.SemiBold)) { append(stringResource(Res.string.report_action)) }
                        },
                        style = bodyStyle(14.sp, color = VentrovaColors.Secondary),
                        modifier = Modifier
                            .padding(top = 14.dp)
                            .align(Alignment.CenterHorizontally)
                            .clickable { viewModel.showMessage(AppMessage.Kind.NotAvailableYet) },
                    )
                }
            }
        }
        Row(Modifier.fillMaxWidth().statusBarsPadding().padding(12.dp), horizontalArrangement = Arrangement.SpaceBetween) {
            FloatingCircleButton(Res.drawable.ic_arrow_back) { viewModel.closeDetail() }
            FloatingCircleButton(Res.drawable.ic_share) { actions.share(place) }
        }
    }
}

@Composable
private fun TonalIconButton(icon: DrawableResource, onClick: () -> Unit) {
    Surface(
        onClick = onClick,
        shape = RoundedCornerShape(16.dp),
        color = VentrovaColors.AccentContainerSoft,
        contentColor = VentrovaColors.OnAccentContainerAlt,
        modifier = Modifier.size(52.dp),
    ) {
        Box(contentAlignment = Alignment.Center) { VIcon(icon, size = 22.dp) }
    }
}

@Composable
private fun FloatingCircleButton(icon: DrawableResource, onClick: () -> Unit) {
    Box(
        Modifier
            .size(48.dp)
            .shadow(2.dp, CircleShape)
            .background(VentrovaColors.Canvas.copy(alpha = 0.92f), CircleShape)
            .clip(CircleShape)
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center,
    ) {
        VIcon(icon, tint = VentrovaColors.Ink)
    }
}
